/**
 * File: apps/qbench-cp/reactapp/src/shared/utils/logger.js
 *
 * Purpose: Sanctioned entry point for reporting caught errors. 14 caught-error console.error()
 *     call sites in this app route through it.
 *
 * Scope: 14 caught-error console.error() call sites (catch blocks and .catch() handlers) route
 *     through this file. It does NOT cover every caught-error console.error() call site in this
 *     app -- exactly 9 remain unrouted elsewhere by the same definition (catch blocks /
 *     .catch() handlers only -- not a plain helper a catch block happens to call, and not a bare
 *     function reference passed as a callback prop): shared/utils/pollUrl.js:33,
 *     shared/components/FileUpload/index.js:63,154,256,308,
 *     Dashboard/Pages/Sources/SourceList/ImportSources/index.js:89, Login/ForgotPwd/index.js
 *     :153,217,279 -- so this is NOT the only place apps/qbench-cp/reactapp code may reach the
 *     console for a caught error, only the sanctioned sink for the sites that route through it.
 *     Also does not cover console.log/console.warn/console.debug/console.info call sites
 *     elsewhere in this app.
 *
 * Tags: customer-portal
 *
 * Overview: Exports logError(context, error), which writes a labeled entry to the browser
 *     console via the one call site this file's own inline `// thailint:
 *     ignore[improper-logging.print-statement]` directive allowlists (see Suppressions below —
 *     the format matches this repo's own established convention for this ignore, e.g.
 *     apps/qbench/static/js/DynamicSpreadsheet/utils.js's toggleMerge catch handler). No
 *     centralized error-reporting service (Sentry or similar) is wired into
 *     apps/qbench-cp/reactapp today — apps/qleap depends on @sentry/react and calls
 *     Sentry.captureException elsewhere in this codebase family (see
 *     apps/qleap/apps/qbench/src/lib/api.ts), but qbench-cp/reactapp carries no such dependency,
 *     and adding one is out of scope for this cleanup. This wrapper is therefore new
 *     infrastructure for this app, not a rediscovered qbench-cp pattern. It exists so its 14
 *     routed caught-error console.error() call sites go through one grep-able call site instead
 *     of calling console.error directly -- a start on making a future swap to real telemetry
 *     easier, not a claim that this file is the only place left to swap (see Scope above for the
 *     sites that don't route through it).
 *
 * Suppressions:
 *     - improper-logging.print-statement (1, logError's own console.error call): the rule's own
 *       documented purpose (.thailint.yaml's print-statements section: "flags print()/console.*
 *       calls that should use logging") is to push ad-hoc, scattered console usage toward a
 *       logging abstraction. This line is not an instance of that -- it IS the logging
 *       abstraction's own implementation, the one place logError's callers' console.error calls
 *       actually land once routed through it (see Overview). A "should use logging instead"
 *       rule cannot coherently also forbid the logging wrapper's own implementation from
 *       reaching the console: something has to be the boundary that actually touches the
 *       underlying primitive, the same way a "no raw SQL, use the ORM" convention does not reach
 *       the ORM driver's own SQL calls -- forbidding this line would not remove a console call
 *       from the app, it would only relocate the identical line to wherever logError's callers
 *       are told to route through next. No telemetry service (Sentry or similar) is wired into
 *       this app to be that boundary instead (see Overview), so this line is it. thai-lint's
 *       print-statements config carries no allowlist/ignore mechanism that would let the linter
 *       recognize a sink's own implementation as distinct from a scattered call site (checked
 *       directly against .thailint.yaml -- the section has no `ignore:` key at all), so its
 *       pattern-match still fires here even though the construct sits outside what the rule's
 *       own stated purpose governs.
 *
 * Exports: logError(context, error) — logs a labeled error to the console.
 */

export function logError(context, error) {
  // Literal first argument, context and error as separate trailing args -- not
  // `` `[${context}]` `` interpolated into the first argument, which semgrep's
  // javascript.lang.security.audit.unsafe-formatstring.unsafe-formatstring rule (correctly, on
  // its own general pattern) flags as a non-literal format string, even though every one of
  // this function's 14 call sites passes context as a hardcoded string literal, never user
  // input.
  console.error('[logError]', context, error); // thailint: ignore[improper-logging.print-statement] - sanctioned sink; see file header Suppressions
}
